import { ArrowDown, ArrowUp, ArrowUpDown, Search, X } from 'lucide-react'
import { useMemo, useState } from 'react'

import Paginacion from '../../ui/Paginacion'
import { usePaginacion } from '../../../hooks/usePaginacion'
import { formatearPorcentaje, formatearVariacionPct } from '../../../utils/analiticaPos'
import {
  formatearFechaPos,
  formatearKilosPos,
  formatearMontoPos,
} from '../../../utils/pos'
import { GraficoRankingHorizontal } from './GraficoBarras'
import { estiloInputBusqueda } from '../FiltrosPosComunes'

const RANKINGS = [
  { valor: 'monto_neto', etiqueta: 'Top 10 por monto vendido', formato: 'monto' },
  { valor: 'kilos_vendidos', etiqueta: 'Top 10 por kilos vendidos', formato: 'kilos' },
  { valor: 'cantidad_ventas', etiqueta: 'Top 10 por cantidad de ventas', formato: 'numero' },
  { valor: 'clientes_distintos', etiqueta: 'Top 10 por número de clientes', formato: 'numero' },
  { valor: 'precio_promedio_kg', etiqueta: 'Top 10 por precio promedio/kg', formato: 'monto' },
  { valor: 'variacion_monto_pct', etiqueta: 'Top 10 mayor crecimiento vs. período anterior', formato: 'pct', filtrarComparables: true, orden: 'desc' },
  { valor: 'variacion_monto_pct_caida', etiqueta: 'Top 10 mayor caída vs. período anterior', formato: 'pct', filtrarComparables: true, orden: 'asc', clave: 'variacion_monto_pct' },
]

const COLUMNAS = [
  { clave: 'producto_nombre', etiqueta: 'Producto' },
  { clave: 'cantidad_ventas', etiqueta: 'Ventas' },
  { clave: 'kilos_vendidos', etiqueta: 'Kg' },
  { clave: 'monto_neto', etiqueta: 'Monto' },
  { clave: 'precio_promedio_kg', etiqueta: 'Precio prom/kg' },
  { clave: 'clientes_distintos', etiqueta: 'Clientes' },
  { clave: 'participacion_monto_pct', etiqueta: 'Particip. %' },
  { clave: 'ultima_venta', etiqueta: 'Última venta' },
]

function formatearValorRanking(valor, formato) {
  if (formato === 'monto') return formatearMontoPos(valor)
  if (formato === 'kilos') return formatearKilosPos(valor)
  if (formato === 'pct') return formatearVariacionPct(valor) || '—'
  return String(Math.round(valor))
}

function ProductosAnalitica({ productos, onSeleccionarProducto, productoSeleccionado }) {
  const [busqueda, setBusqueda] = useState('')
  const [ordenPor, setOrdenPor] = useState('monto_neto')
  const [ordenAsc, setOrdenAsc] = useState(false)
  const [rankingActivo, setRankingActivo] = useState('monto_neto')

  const terminoBusqueda = busqueda.trim().toLowerCase()

  const filtrados = useMemo(() => {
    if (!terminoBusqueda) return productos

    return productos.filter(
      (p) =>
        p.producto_nombre?.toLowerCase().includes(terminoBusqueda) ||
        p.producto_codigo?.toLowerCase().includes(terminoBusqueda)
    )
  }, [productos, terminoBusqueda])

  const ordenados = useMemo(() => {
    return [...filtrados].sort((a, b) => {
      const valorA = a[ordenPor]
      const valorB = b[ordenPor]

      if (valorA === valorB) return 0
      if (valorA === null || valorA === undefined) return 1
      if (valorB === null || valorB === undefined) return -1

      const comparacion = typeof valorA === 'string' ? valorA.localeCompare(valorB) : valorA - valorB

      return ordenAsc ? comparacion : -comparacion
    })
  }, [filtrados, ordenPor, ordenAsc])

  const filtrosKey = JSON.stringify([terminoBusqueda])
  const { pagina, setPagina, tamanioPagina, setTamanioPagina, totalPaginas, indiceInicio, indiceFin } =
    usePaginacion({ totalRegistros: ordenados.length, filtrosKey })

  const paginados = useMemo(() => {
    const inicio = (pagina - 1) * tamanioPagina
    return ordenados.slice(inicio, inicio + tamanioPagina)
  }, [ordenados, pagina, tamanioPagina])

  const alternarOrden = (clave) => {
    if (ordenPor === clave) {
      setOrdenAsc((actual) => !actual)
    } else {
      setOrdenPor(clave)
      setOrdenAsc(false)
    }
  }

  const configRanking = RANKINGS.find((r) => r.valor === rankingActivo) || RANKINGS[0]

  const datosRanking = useMemo(() => {
    const clave = configRanking.clave || configRanking.valor
    let base = productos.filter((p) => p[clave] !== null && p[clave] !== undefined)

    if (configRanking.filtrarComparables) {
      base = base.filter((p) => p.monto_periodo_anterior !== null && p.monto_periodo_anterior > 0)
    }

    const ordenDireccion = configRanking.orden === 'asc' ? 1 : -1

    return base
      .sort((a, b) => (a[clave] - b[clave]) * ordenDireccion)
      .slice(0, 10)
      .map((p) => ({
        etiqueta: p.producto_nombre,
        valor: p[clave],
      }))
  }, [productos, configRanking])

  if (productos.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
        <p className="text-slate-500">No hay productos vendidos para los filtros seleccionados.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-base font-bold text-slate-900">Rankings de productos</h3>

          <select
            value={rankingActivo}
            onChange={(event) => setRankingActivo(event.target.value)}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
          >
            {RANKINGS.map((r) => (
              <option key={r.etiqueta} value={r.valor}>
                {r.etiqueta}
              </option>
            ))}
          </select>
        </div>

        {datosRanking.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">
            {configRanking.filtrarComparables
              ? 'Sin período anterior comparable para este ranking.'
              : 'Sin datos.'}
          </p>
        ) : (
          <GraficoRankingHorizontal
            datos={datosRanking}
            formatearValor={(valor) => formatearValorRanking(valor, configRanking.formato)}
          />
        )}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-base font-bold text-slate-900">Detalle por producto</h3>

          <div className="relative w-full sm:w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={busqueda}
              onChange={(event) => setBusqueda(event.target.value)}
              placeholder="Buscar producto..."
              className={estiloInputBusqueda}
            />
          </div>
        </div>

        {productoSeleccionado && (
          <div className="mb-4 flex items-center justify-between gap-3 rounded-lg bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700">
            Producto seleccionado para evolución/clientes: {productoSeleccionado.producto_nombre}
            <button type="button" onClick={() => onSeleccionarProducto(null)} className="text-emerald-600 hover:text-emerald-800">
              <X size={16} />
            </button>
          </div>
        )}

        <div className="w-full overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                {COLUMNAS.map((col) => (
                  <th key={col.clave} className="whitespace-nowrap px-3 py-2">
                    <button
                      type="button"
                      onClick={() => alternarOrden(col.clave)}
                      className="flex items-center gap-1 hover:text-slate-800"
                    >
                      {col.etiqueta}
                      {ordenPor === col.clave ? (
                        ordenAsc ? <ArrowUp size={12} /> : <ArrowDown size={12} />
                      ) : (
                        <ArrowUpDown size={12} className="text-slate-300" />
                      )}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {paginados.map((p) => (
                <tr
                  key={p.producto_id}
                  onClick={() => onSeleccionarProducto(p)}
                  className={`cursor-pointer transition hover:bg-slate-50 ${
                    productoSeleccionado?.producto_id === p.producto_id ? 'bg-emerald-50' : ''
                  }`}
                >
                  <td className="whitespace-nowrap px-3 py-2.5 font-semibold text-slate-900">{p.producto_nombre}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">{p.cantidad_ventas}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">{formatearKilosPos(p.kilos_vendidos)}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 font-semibold text-slate-900">{formatearMontoPos(p.monto_neto)}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">{formatearMontoPos(p.precio_promedio_kg)}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">{p.clientes_distintos}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">{formatearPorcentaje(p.participacion_monto_pct)}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">{formatearFechaPos(p.ultima_venta)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Paginacion
          pagina={pagina}
          totalPaginas={totalPaginas}
          tamanioPagina={tamanioPagina}
          totalRegistros={ordenados.length}
          indiceInicio={indiceInicio}
          indiceFin={indiceFin}
          etiquetaRegistros="productos"
          onCambiarPagina={setPagina}
          onCambiarTamanio={setTamanioPagina}
        />
      </div>
    </div>
  )
}

export default ProductosAnalitica
